/**
 * Security Assistant Sidebar
 *
 * A persistent right sidebar panel that provides AI-powered security assistance.
 * Always visible in the layout, can be collapsed to save space.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Badge,
  Chip,
  Button,
  ButtonGroup,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
  Divider,
  Card,
  CardContent,
  LinearProgress,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Lightbulb as LightbulbIcon,
  Security as SecurityIcon,
  Shield as ShieldIcon,
  BugReport as BugReportIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Psychology as PsychologyIcon,
  Radar as RadarIcon,
  VerifiedUser as VerifiedUserIcon,
  PlayArrow as PlayArrowIcon,
  Info as InfoIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Link as LinkIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import type {
  AssistantSuggestion,
  AttackScenarioAnalysis,
  AssistantContext,
  ComponentContext,
  RiskContext,
  Relation,
  Component,
  Risk,
} from 'shared';
import { SuggestionPriority } from 'shared';
import { useSecurityAssistant } from '../../contexts/SecurityAssistantContext';
import { relationsService } from '../../services/relations.service';
import {
  componentsService,
  type ComponentThreatProfile,
  type SubtechniqueInfo,
  type ThreatTemplate,
} from '../../services/components.service';
import { riskApi } from '../../services/risk.service';
import { useCreateRiskFromThreat } from '../../hooks/useCreateRiskFromThreat';

// =============================================================================
// CONSTANTS
// =============================================================================

export const SIDEBAR_WIDTH = 340;
export const SIDEBAR_COLLAPSED_WIDTH = 48;

const PRIORITY_COLORS = {
  [SuggestionPriority.HIGH]: '#FE3B00',
  [SuggestionPriority.MEDIUM]: '#FFBB00',
  [SuggestionPriority.LOW]: '#42B8B2',
};

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

interface PriorityChipProps {
  priority: SuggestionPriority;
}

const PriorityChip: React.FC<PriorityChipProps> = ({ priority }) => {
  const { t } = useTranslation();
  const color =
    PRIORITY_COLORS[priority] || PRIORITY_COLORS[SuggestionPriority.MEDIUM];

  return (
    <Chip
      label={t(`assistant.priority.${priority}`)}
      size="small"
      sx={{
        backgroundColor: `${color}20`,
        color: color,
        fontWeight: 600,
        fontSize: '0.7rem',
      }}
    />
  );
};

interface ConfidenceBarProps {
  confidence: number;
}

const ConfidenceBar: React.FC<ConfidenceBarProps> = ({ confidence }) => {
  const { t } = useTranslation();
  const percentage = Math.round(confidence * 100);
  const color =
    confidence >= 0.8 ? '#42B8B2' : confidence >= 0.5 ? '#FFBB00' : '#939597';

  return (
    <Tooltip title={t('assistant.confidence', { value: percentage })}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 60 }}
      >
        <Box
          sx={{
            flex: 1,
            height: 4,
            borderRadius: 2,
            backgroundColor: 'action.disabledBackground',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${percentage}%`,
              height: '100%',
              backgroundColor: color,
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {percentage}%
        </Typography>
      </Box>
    </Tooltip>
  );
};

const SuggestionTypeIcon: React.FC<{ type: string }> = ({ type }) => {
  switch (type) {
    case 'risk':
      return <WarningIcon fontSize="small" sx={{ color: '#FE3B00' }} />;
    case 'mitigation':
      return <ShieldIcon fontSize="small" sx={{ color: '#42B8B2' }} />;
    case 'component':
      return <BugReportIcon fontSize="small" sx={{ color: '#2D3B45' }} />;
    case 'attack_scenario':
      return <RadarIcon fontSize="small" sx={{ color: '#FE3B00' }} />;
    case 'compliance':
      return <VerifiedUserIcon fontSize="small" sx={{ color: '#FFBB00' }} />;
    default:
      return <LightbulbIcon fontSize="small" sx={{ color: '#FFBB00' }} />;
  }
};

// =============================================================================
// SUGGESTION CARD
// =============================================================================

interface SuggestionCardProps {
  suggestion: AssistantSuggestion;
  onAccept: (suggestion: AssistantSuggestion) => void;
  onDismiss: (id: string) => void;
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({
  suggestion,
  onAccept,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const hasReferences =
    suggestion.attackTechniqueId ||
    suggestion.d3fendId ||
    suggestion.isoControlId ||
    suggestion.cweId;

  return (
    <Card
      sx={{
        mb: 1,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 1,
        },
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            mb: 1,
          }}
        >
          <SuggestionTypeIcon type={suggestion.type} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {suggestion.title}
            </Typography>
          </Box>
          <PriorityChip priority={suggestion.priority} />
        </Box>

        {/* Confidence */}
        <Box sx={{ mb: 1 }}>
          <ConfidenceBar confidence={suggestion.confidence} />
        </Box>

        {/* Expandable details */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {suggestion.description}
            </Typography>

            {suggestion.reasoning && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                >
                  {t('assistant.reasoning')}
                </Typography>
                <Typography variant="caption" display="block">
                  {suggestion.reasoning}
                </Typography>
              </Box>
            )}

            {hasReferences && (
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {suggestion.attackTechniqueId && (
                  <Chip
                    size="small"
                    label={suggestion.attackTechniqueId}
                    variant="outlined"
                    sx={{ fontSize: '0.65rem' }}
                  />
                )}
                {suggestion.isoControlId && (
                  <Chip
                    size="small"
                    label={suggestion.isoControlId}
                    variant="outlined"
                    sx={{ fontSize: '0.65rem' }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Collapse>

        {/* Actions */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 1,
          }}
        >
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            color="error"
            onClick={() => onDismiss(suggestion.id)}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            {t('assistant.dismiss')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => onAccept(suggestion)}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            {t('assistant.accept')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// ATTACK SCENARIO CARD
// =============================================================================

interface AttackScenarioCardProps {
  scenario: AttackScenarioAnalysis;
}

const AttackScenarioCard: React.FC<AttackScenarioCardProps> = ({
  scenario,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const riskColor =
    scenario.riskScore >= 7
      ? '#FE3B00'
      : scenario.riskScore >= 4
        ? '#FFBB00'
        : '#42B8B2';

  return (
    <Card
      sx={{
        mb: 1,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <RadarIcon sx={{ color: riskColor }} fontSize="small" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {scenario.name}
            </Typography>
            <Chip
              size="small"
              label={`${t('assistant.riskScore')}: ${scenario.riskScore.toFixed(1)}`}
              sx={{
                mt: 0.5,
                backgroundColor: `${riskColor}20`,
                color: riskColor,
                fontWeight: 600,
                fontSize: '0.65rem',
              }}
            />
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary">
          {scenario.description}
        </Typography>

        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('assistant.steps')}: {scenario.attackPath.length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('assistant.affected')}: {scenario.affectedComponents.length}
          </Typography>
        </Box>

        <Button
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{ mt: 1, minWidth: 'auto', px: 1 }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          {expanded ? t('common.close') : t('common.open')}
        </Button>

        <Collapse in={expanded}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" fontWeight={600}>
            {t('assistant.attackPath')}
          </Typography>
          <List dense disablePadding>
            {scenario.attackPath.map((step, idx) => (
              <ListItem key={step.stepNumber} sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <Typography variant="caption" color="text.secondary">
                    {idx + 1}.
                  </Typography>
                </ListItemIcon>
                <ListItemText
                  primary={step.technique}
                  secondary={step.description}
                  primaryTypographyProps={{ variant: 'caption' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>

          {scenario.suggestedMitigations.length > 0 && (
            <>
              <Typography variant="caption" fontWeight={600} sx={{ mt: 1 }}>
                {t('assistant.suggestedMitigations')}
              </Typography>
              <List dense disablePadding>
                {scenario.suggestedMitigations.map((mit, idx) => (
                  <ListItem key={idx} sx={{ px: 0, py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <ShieldIcon sx={{ fontSize: 14, color: '#42B8B2' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={mit}
                      primaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// INSIGHTS TAB COMPONENT - Relations and connected risk status
// =============================================================================

interface RelationInfo {
  relation: Relation;
  otherEntityId: string;
  connectedComponent: Component | null;
  risks: Risk[];
}

interface InsightsData {
  loading: boolean;
  error: string | null;
  relations: RelationInfo[];
  incomingCount: number;
  outgoingCount: number;
  criticalConnections: number;
  componentRisks: Risk[];
  threatProfile: ComponentThreatProfile | null;
}

interface ExpandedThreats {
  [techniqueId: string]: {
    loading: boolean;
    subtechniques: SubtechniqueInfo[];
  };
}

interface InsightsTabProps {
  context: AssistantContext;
  onComponentClick?: (component: Component) => void;
}

const InsightsTab: React.FC<InsightsTabProps> = ({
  context,
  onComponentClick,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showAllRisks, setShowAllRisks] = useState(false);
  const [showAllThreats, setShowAllThreats] = useState(false);
  const [expandedThreats, setExpandedThreats] = useState<ExpandedThreats>({});
  const [creatingRiskFor, setCreatingRiskFor] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [insightsData, setInsightsData] = useState<InsightsData>({
    loading: false,
    error: null,
    relations: [],
    incomingCount: 0,
    outgoingCount: 0,
    criticalConnections: 0,
    componentRisks: [],
    threatProfile: null,
  });

  // Hook for creating risks from threats
  const { createRiskFromThreat, isCreating } = useCreateRiskFromThreat();

  // Handler for creating a risk from a threat or subtechnique
  const handleCreateRisk = async (
    threat: ThreatTemplate,
    subtechnique?: SubtechniqueInfo
  ) => {
    if (context.type !== 'component') return;

    const componentContext = context as ComponentContext;
    const techniqueId = subtechnique?.id || threat.attackTechniqueId;
    setCreatingRiskFor(techniqueId);

    const result = await createRiskFromThreat({
      componentId: componentContext.componentId,
      spaceId: componentContext.spaceId || '',
      threat: subtechnique ? undefined : threat,
      subtechnique,
      parentThreat: subtechnique ? threat : undefined,
    });

    setCreatingRiskFor(null);

    if (result) {
      setSuccessMessage(
        t('assistant.insights.riskCreated', { title: result.title })
      );
      // Refresh component risks
      const updatedRisks = await riskApi.getByComponent(
        componentContext.componentId
      );
      setInsightsData(prev => ({
        ...prev,
        componentRisks: updatedRisks.sort((a, b) => {
          const aScore = Number(a.residualRisk) || Number(a.inherentRisk) || 0;
          const bScore = Number(b.residualRisk) || Number(b.inherentRisk) || 0;
          return bScore - aScore;
        }),
      }));
      // Navigate to the new risk
      setTimeout(() => {
        navigate(`/risks/${result.id}`);
      }, 1000);
    }
  };

  // Handler for toggling subtechniques expansion
  const handleToggleSubtechniques = async (
    techniqueId: string,
    componentId: string
  ) => {
    // If already expanded, collapse
    if (expandedThreats[techniqueId]) {
      setExpandedThreats(prev => {
        const newState = { ...prev };
        delete newState[techniqueId];
        return newState;
      });
      return;
    }

    // Set loading state
    setExpandedThreats(prev => ({
      ...prev,
      [techniqueId]: { loading: true, subtechniques: [] },
    }));

    try {
      const subtechniques = await componentsService.getSubtechniques(
        componentId,
        techniqueId
      );
      setExpandedThreats(prev => ({
        ...prev,
        [techniqueId]: { loading: false, subtechniques },
      }));
    } catch (err) {
      console.error('Failed to fetch subtechniques:', err);
      setExpandedThreats(prev => ({
        ...prev,
        [techniqueId]: { loading: false, subtechniques: [] },
      }));
    }
  };

  // Fetch relations and connected component data
  useEffect(() => {
    const fetchInsightsData = async () => {
      if (context.type !== 'component') {
        setInsightsData(prev => ({
          ...prev,
          loading: false,
          relations: [],
          incomingCount: 0,
          outgoingCount: 0,
          criticalConnections: 0,
          componentRisks: [],
          threatProfile: null,
        }));
        return;
      }

      const componentContext = context as ComponentContext;
      if (!componentContext.componentId) return;

      setInsightsData(prev => ({ ...prev, loading: true, error: null }));

      try {
        // Fetch threat profile for this component type
        const threatProfilePromise = componentsService.getThreatProfile(
          componentContext.componentId
        );

        // Fetch relations for this component
        const allRelations = await relationsService.getByEntityId(
          componentContext.componentId
        );

        // Filter out non-component relations (HAS_RISK, BELONGS_TO, etc.)
        // Only keep relations between components (infrastructure, data flow, etc.)
        const NON_COMPONENT_RELATIONS = [
          'HAS_RISK',
          'MITIGATED_BY',
          'THREAT',
          'EXPLOITS',
          'IMPLEMENTS_CONTROL',
          'HAS_CONTROL_GAP',
          'RESOLVED_BY',
          'BELONGS_TO',
          'OWNED_BY',
          'MAINTAINED_BY',
          'RESPONSIBLE_FOR',
          'DOCUMENTED_BY',
          'FOLLOWS_PROCESS',
          'HAS_PROCEDURE',
        ];
        const relations = allRelations.filter(
          rel => !NON_COMPONENT_RELATIONS.includes(rel.type)
        );

        // Fetch risks for this component
        const componentRisks = await riskApi.getByComponent(
          componentContext.componentId
        );

        // Get unique connected component IDs
        const connectedIds = new Set<string>();
        relations.forEach(rel => {
          if (rel.fromEntityId !== componentContext.componentId) {
            connectedIds.add(rel.fromEntityId);
          }
          if (rel.toEntityId !== componentContext.componentId) {
            connectedIds.add(rel.toEntityId);
          }
        });

        // Fetch connected components
        const connectedComponents = await componentsService.getByIds(
          Array.from(connectedIds)
        );
        const componentMap = new Map(connectedComponents.map(c => [c.id, c]));

        // Fetch risks for connected components
        const connectedRisksPromises = Array.from(connectedIds).map(id =>
          riskApi.getByComponent(id).catch(() => [] as Risk[])
        );
        const connectedRisksArrays = await Promise.all(connectedRisksPromises);
        const connectedRisksMap = new Map<string, Risk[]>();
        Array.from(connectedIds).forEach((id, idx) => {
          connectedRisksMap.set(id, connectedRisksArrays[idx]);
        });

        // Build relation info
        const relationInfos: RelationInfo[] = relations.map(rel => {
          const otherEntityId =
            rel.fromEntityId === componentContext.componentId
              ? rel.toEntityId
              : rel.fromEntityId;
          return {
            relation: rel,
            otherEntityId,
            connectedComponent: componentMap.get(otherEntityId) || null,
            risks: connectedRisksMap.get(otherEntityId) || [],
          };
        });

        // Count incoming vs outgoing
        const incomingCount = relations.filter(
          r => r.toEntityId === componentContext.componentId
        ).length;
        const outgoingCount = relations.filter(
          r => r.fromEntityId === componentContext.componentId
        ).length;

        // Count connections to components with high/critical risks
        const criticalConnections = relationInfos.filter(info =>
          info.risks.some(r => (r.residualRisk || r.inherentRisk || 0) >= 7)
        ).length;

        // Sort risks by residual risk (descending) - highest risk first
        const sortedComponentRisks = [...componentRisks].sort((a, b) => {
          const aScore = Number(a.residualRisk) || Number(a.inherentRisk) || 0;
          const bScore = Number(b.residualRisk) || Number(b.inherentRisk) || 0;
          return bScore - aScore;
        });

        // Wait for threat profile
        const threatProfile = await threatProfilePromise.catch(() => null);

        setInsightsData({
          loading: false,
          error: null,
          relations: relationInfos,
          incomingCount,
          outgoingCount,
          criticalConnections,
          componentRisks: sortedComponentRisks,
          threatProfile,
        });
      } catch (err) {
        console.error('Failed to fetch insights data:', err);
        setInsightsData(prev => ({
          ...prev,
          loading: false,
          error: t('assistant.insights.loadError'),
        }));
      }
    };

    fetchInsightsData();
  }, [context, t]);

  if (context.type === 'none') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 2,
        }}
      >
        <InfoIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
        <Typography variant="body2" color="text.secondary">
          {t('assistant.selectContext')}
        </Typography>
      </Box>
    );
  }

  const getRiskLevel = (score: number) => {
    if (score >= 7)
      return { label: t('common.priority.critical'), color: '#FF5252' };
    if (score >= 5)
      return { label: t('common.priority.high'), color: '#2D3B45' };
    if (score >= 3)
      return { label: t('common.priority.medium'), color: '#FFB300' };
    return { label: t('common.priority.low'), color: '#00D9FF' };
  };

  // Component context insights with relations
  if (context.type === 'component') {
    const {
      loading,
      error,
      relations,
      incomingCount,
      outgoingCount,
      criticalConnections,
      componentRisks,
    } = insightsData;

    // Find highest risk score among direct risks
    const highestRiskScore =
      componentRisks.length > 0
        ? Math.max(
            ...componentRisks.map(r => r.residualRisk || r.inherentRisk || 0)
          )
        : 0;

    return (
      <Box>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: '0.75rem' }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* Connection Summary */}
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ mb: 1, display: 'block' }}
            >
              {t('assistant.insights.connections')}
            </Typography>
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                backgroundColor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color="primary.main"
                  >
                    {relations.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('assistant.insights.totalRelations')}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <ArrowBackIcon
                      sx={{ fontSize: 14, color: 'text.secondary' }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {incomingCount}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('assistant.insights.incoming')}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {outgoingCount}
                    </Typography>
                    <ArrowForwardIcon
                      sx={{ fontSize: 14, color: 'text.secondary' }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('assistant.insights.outgoing')}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Critical Risk Connections Warning */}
            {criticalConnections > 0 && (
              <Alert
                severity="warning"
                icon={<WarningIcon fontSize="small" />}
                sx={{
                  mb: 2,
                  py: 0.5,
                  '& .MuiAlert-message': { fontSize: '0.75rem' },
                }}
              >
                {t('assistant.insights.criticalConnections', {
                  count: criticalConnections,
                })}
              </Alert>
            )}

            {/* Direct Risks on this Component */}
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ mb: 1, display: 'block' }}
            >
              {t('assistant.insights.directRisks')}
            </Typography>
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                backgroundColor: 'action.hover',
                borderRadius: 1,
              }}
            >
              {componentRisks.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {t('assistant.insights.noDirectRisks')}
                </Typography>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {componentRisks.length}{' '}
                      {t('assistant.insights.risksIdentified')}
                    </Typography>
                    {highestRiskScore > 0 && (
                      <Chip
                        size="small"
                        label={`${t('assistant.insights.highest')}: ${getRiskLevel(highestRiskScore).label}`}
                        sx={{
                          backgroundColor: getRiskLevel(highestRiskScore).color,
                          color: 'white',
                          fontSize: '0.65rem',
                          height: 20,
                        }}
                      />
                    )}
                  </Box>
                  <List dense disablePadding>
                    {(showAllRisks
                      ? componentRisks
                      : componentRisks.slice(0, 3)
                    ).map(risk => (
                      <ListItem
                        key={risk.id}
                        sx={{
                          px: 0,
                          py: 0.25,
                          cursor: 'pointer',
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                        onClick={() => navigate(`/risks/${risk.id}`)}
                      >
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <WarningIcon
                            sx={{
                              fontSize: 14,
                              color: getRiskLevel(
                                Number(risk.residualRisk) ||
                                  Number(risk.inherentRisk) ||
                                  0
                              ).color,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={risk.title}
                          primaryTypographyProps={{
                            variant: 'caption',
                            noWrap: true,
                          }}
                        />
                        <ChevronRightIcon
                          sx={{ fontSize: 14, color: 'text.secondary' }}
                        />
                      </ListItem>
                    ))}
                    {componentRisks.length > 3 && (
                      <Typography
                        variant="caption"
                        color="primary"
                        sx={{
                          pl: 3,
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                        onClick={() => setShowAllRisks(!showAllRisks)}
                      >
                        {showAllRisks
                          ? t('common.showLess')
                          : `+${componentRisks.length - 3} ${t('common.more')}`}
                      </Typography>
                    )}
                  </List>
                </>
              )}
            </Box>

            {/* ATT&CK Threat Profile */}
            {insightsData.threatProfile &&
              insightsData.threatProfile.threats.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{ mb: 1, display: 'block' }}
                  >
                    {t('assistant.insights.threatProfile')}
                  </Typography>
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      backgroundColor: 'action.hover',
                      borderRadius: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <BugReportIcon
                        sx={{ fontSize: 16, color: 'error.main' }}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {insightsData.threatProfile.threats.length}{' '}
                        {t('assistant.insights.attackTechniques')}
                      </Typography>
                    </Box>
                    <List dense disablePadding>
                      {(showAllThreats
                        ? insightsData.threatProfile.threats
                        : insightsData.threatProfile.threats.slice(0, 3)
                      ).map(threat => {
                        const isExpanded =
                          !!expandedThreats[threat.attackTechniqueId];
                        const subtechniqueData =
                          expandedThreats[threat.attackTechniqueId];

                        return (
                          <Box key={threat.id}>
                            <ListItem
                              sx={{
                                px: 0,
                                py: 0.25,
                                cursor:
                                  threat.subtechniqueCount > 0
                                    ? 'pointer'
                                    : 'default',
                                borderRadius: 1,
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                              }}
                              onClick={() => {
                                if (threat.subtechniqueCount > 0) {
                                  handleToggleSubtechniques(
                                    threat.attackTechniqueId,
                                    (context as ComponentContext).componentId
                                  );
                                }
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 24 }}>
                                {threat.subtechniqueCount > 0 ? (
                                  isExpanded ? (
                                    <ExpandLessIcon sx={{ fontSize: 16 }} />
                                  ) : (
                                    <ExpandMoreIcon sx={{ fontSize: 16 }} />
                                  )
                                ) : (
                                  <RadarIcon
                                    sx={{ fontSize: 14, color: 'error.main' }}
                                  />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    <Tooltip title={threat.attackTechniqueName}>
                                      <Typography
                                        variant="caption"
                                        fontWeight={500}
                                        noWrap
                                        sx={{ maxWidth: 140 }}
                                      >
                                        {threat.attackTechniqueId}
                                      </Typography>
                                    </Tooltip>
                                    {threat.subtechniqueCount > 0 && (
                                      <Chip
                                        label={`+${threat.subtechniqueCount}`}
                                        size="small"
                                        sx={{
                                          fontSize: '0.6rem',
                                          height: 16,
                                          bgcolor: 'warning.light',
                                        }}
                                      />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                  >
                                    {threat.attackTechniqueName}
                                  </Typography>
                                }
                              />
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip
                                  title={t('assistant.insights.createRisk')}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleCreateRisk(threat);
                                    }}
                                    disabled={
                                      isCreating ||
                                      creatingRiskFor ===
                                        threat.attackTechniqueId
                                    }
                                    sx={{
                                      p: 0.25,
                                      color: 'success.main',
                                      '&:hover': {
                                        backgroundColor: 'success.light',
                                      },
                                    }}
                                  >
                                    {creatingRiskFor ===
                                    threat.attackTechniqueId ? (
                                      <CircularProgress size={12} />
                                    ) : (
                                      <AddIcon sx={{ fontSize: 14 }} />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title={t('assistant.insights.viewOnMitre')}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={e => {
                                      e.stopPropagation();
                                      window.open(
                                        threat.attackTechniqueUrl,
                                        '_blank'
                                      );
                                    }}
                                    sx={{ p: 0.25 }}
                                  >
                                    <ChevronRightIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </ListItem>

                            {/* Subtechniques */}
                            <Collapse in={isExpanded}>
                              {subtechniqueData?.loading ? (
                                <Box sx={{ pl: 4, py: 0.5 }}>
                                  <CircularProgress size={12} />
                                </Box>
                              ) : (
                                <List dense disablePadding sx={{ pl: 3 }}>
                                  {subtechniqueData?.subtechniques.map(sub => (
                                    <ListItem
                                      key={sub.id}
                                      sx={{
                                        px: 0,
                                        py: 0.25,
                                        borderRadius: 1,
                                        '&:hover': {
                                          backgroundColor: 'action.hover',
                                        },
                                      }}
                                    >
                                      <ListItemIcon sx={{ minWidth: 20 }}>
                                        <RadarIcon
                                          sx={{
                                            fontSize: 12,
                                            color: 'warning.main',
                                          }}
                                        />
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={
                                          <Tooltip title={sub.name}>
                                            <Typography
                                              variant="caption"
                                              noWrap
                                              sx={{
                                                fontSize: '0.7rem',
                                                maxWidth: 100,
                                                display: 'block',
                                              }}
                                            >
                                              {sub.id}
                                            </Typography>
                                          </Tooltip>
                                        }
                                      />
                                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                                        {sub.countermeasures.length > 0 && (
                                          <Tooltip
                                            title={`${sub.countermeasures.length} D3FEND`}
                                          >
                                            <Chip
                                              label={sub.countermeasures.length}
                                              size="small"
                                              sx={{
                                                fontSize: '0.55rem',
                                                height: 14,
                                                bgcolor: 'success.light',
                                              }}
                                            />
                                          </Tooltip>
                                        )}
                                        <Tooltip
                                          title={t(
                                            'assistant.insights.createRisk'
                                          )}
                                        >
                                          <IconButton
                                            size="small"
                                            onClick={e => {
                                              e.stopPropagation();
                                              handleCreateRisk(threat, sub);
                                            }}
                                            disabled={
                                              isCreating ||
                                              creatingRiskFor === sub.id
                                            }
                                            sx={{
                                              p: 0.25,
                                              color: 'success.main',
                                              '&:hover': {
                                                backgroundColor:
                                                  'success.light',
                                              },
                                            }}
                                          >
                                            {creatingRiskFor === sub.id ? (
                                              <CircularProgress size={10} />
                                            ) : (
                                              <AddIcon sx={{ fontSize: 12 }} />
                                            )}
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                          title={t(
                                            'assistant.insights.viewOnMitre'
                                          )}
                                        >
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              window.open(sub.url, '_blank')
                                            }
                                            sx={{ p: 0.25 }}
                                          >
                                            <ChevronRightIcon
                                              sx={{ fontSize: 12 }}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </ListItem>
                                  ))}
                                </List>
                              )}
                            </Collapse>
                          </Box>
                        );
                      })}
                      {insightsData.threatProfile.threats.length > 3 && (
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{
                            pl: 3,
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                          onClick={() => setShowAllThreats(!showAllThreats)}
                        >
                          {showAllThreats
                            ? t('common.showLess')
                            : `+${insightsData.threatProfile.threats.length - 3} ${t('common.more')}`}
                        </Typography>
                      )}
                    </List>
                  </Box>
                </>
              )}

            {/* Connected Components with Risks */}
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ mb: 1, display: 'block' }}
            >
              {t('assistant.insights.connectedComponents')}
            </Typography>
            {relations.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                {t('assistant.insights.noConnections')}
              </Typography>
            ) : (
              <List
                dense
                disablePadding
                sx={{ maxHeight: 200, overflow: 'auto' }}
              >
                {relations.map(
                  ({ relation, otherEntityId, connectedComponent, risks }) => {
                    const hasHighRisk = risks.some(
                      r => (r.residualRisk || r.inherentRisk || 0) >= 7
                    );
                    const hasMediumRisk = risks.some(r => {
                      const score = r.residualRisk || r.inherentRisk || 0;
                      return score >= 3 && score < 7;
                    });
                    const isClickable = connectedComponent && onComponentClick;

                    return (
                      <ListItem
                        key={relation.id}
                        onClick={
                          isClickable
                            ? () => onComponentClick(connectedComponent)
                            : undefined
                        }
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          mb: 0.5,
                          backgroundColor: hasHighRisk
                            ? 'error.light'
                            : hasMediumRisk
                              ? 'warning.light'
                              : 'action.hover',
                          opacity: hasHighRisk ? 1 : hasMediumRisk ? 0.9 : 0.8,
                          cursor: isClickable ? 'pointer' : 'default',
                          '&:hover': isClickable
                            ? {
                                backgroundColor: hasHighRisk
                                  ? 'error.main'
                                  : hasMediumRisk
                                    ? 'warning.main'
                                    : 'action.selected',
                                '& .MuiTypography-root': {
                                  color:
                                    hasHighRisk || hasMediumRisk
                                      ? 'white'
                                      : undefined,
                                },
                              }
                            : {},
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <LinkIcon
                            sx={{ fontSize: 16, color: 'text.secondary' }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                fontWeight={500}
                                noWrap
                                sx={{ maxWidth: 120 }}
                              >
                                {connectedComponent?.name ||
                                  otherEntityId.slice(0, 8)}
                              </Typography>
                              <Chip
                                label={relation.type.replace(/_/g, ' ')}
                                size="small"
                                sx={{ fontSize: '0.6rem', height: 16 }}
                              />
                            </Box>
                          }
                          secondary={
                            risks.length > 0 ? (
                              <Typography
                                variant="caption"
                                color={
                                  hasHighRisk ? 'error.main' : 'text.secondary'
                                }
                              >
                                {risks.length} {t('assistant.insights.risks')}
                                {hasHighRisk &&
                                  ` • ${t('common.priority.critical')}`}
                              </Typography>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {t('assistant.insights.noRisks')}
                              </Typography>
                            )
                          }
                        />
                      </ListItem>
                    );
                  }
                )}
              </List>
            )}
          </>
        )}

        {/* Success Snackbar */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={4000}
          onClose={() => setSuccessMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSuccessMessage(null)}
            severity="success"
            sx={{ width: '100%' }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // Risk context insights
  if (context.type === 'risk') {
    const riskContext = context as RiskContext;

    return (
      <Box>
        {/* Risk Scores */}
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ mb: 1, display: 'block' }}
        >
          {t('assistant.insights.riskScores')}
        </Typography>

        <Box
          sx={{
            mb: 2,
            p: 1.5,
            backgroundColor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('risk.scores.inherent')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                size="small"
                label={
                  getRiskLevel(Number(riskContext.inherentRisk) || 0).label
                }
                sx={{
                  backgroundColor: getRiskLevel(
                    Number(riskContext.inherentRisk) || 0
                  ).color,
                  color: 'white',
                  fontSize: '0.65rem',
                  height: 20,
                }}
              />
              <Typography variant="body2" fontWeight={600}>
                {(Number(riskContext.inherentRisk) || 0).toFixed(1)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              {t('risk.scores.residual')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                size="small"
                label={
                  getRiskLevel(Number(riskContext.residualRisk) || 0).label
                }
                sx={{
                  backgroundColor: getRiskLevel(
                    Number(riskContext.residualRisk) || 0
                  ).color,
                  color: 'white',
                  fontSize: '0.65rem',
                  height: 20,
                }}
              />
              <Typography variant="body2" fontWeight={600}>
                {(Number(riskContext.residualRisk) || 0).toFixed(1)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Threat Source */}
        {riskContext.threatSource && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ mb: 0.5, display: 'block' }}
            >
              {t('risk.fields.threatSource')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {riskContext.threatSource}
            </Typography>
          </Box>
        )}

        {/* Vulnerability */}
        {riskContext.vulnerability && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ mb: 0.5, display: 'block' }}
            >
              {t('risk.fields.vulnerability')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {riskContext.vulnerability}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Default for other context types
  return (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <InfoIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
      <Typography variant="body2" color="text.secondary">
        {t('assistant.insights.contextInfo', { type: context.type })}
      </Typography>
    </Box>
  );
};

// =============================================================================
// MAIN SIDEBAR COMPONENT
// =============================================================================

interface SecurityAssistantSidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const SecurityAssistantSidebar: React.FC<
  SecurityAssistantSidebarProps
> = ({ collapsed: controlledCollapsed, onToggleCollapse }) => {
  const { t } = useTranslation();
  const {
    state,
    activeTab,
    setActiveTab,
    pendingSuggestionCount,
    acceptSuggestion,
    dismissSuggestion,
    analyzeContext,
    runAttackSimulation,
    setComponentContext,
  } = useSecurityAssistant();

  // Use controlled or local collapsed state
  const [localCollapsed, setLocalCollapsed] = useState(false);
  const collapsed = controlledCollapsed ?? localCollapsed;
  const toggleCollapsed =
    onToggleCollapse ?? (() => setLocalCollapsed(prev => !prev));

  // Snackbar state for feedback
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  // Extract values from state
  const { currentContext, suggestions, attackScenarios, isAnalyzing } = state;

  const contextName = (() => {
    switch (currentContext.type) {
      case 'component':
        return (
          (currentContext as { componentName?: string }).componentName || ''
        );
      case 'risk':
        return (currentContext as { riskTitle?: string }).riskTitle || '';
      case 'mitigation':
        return (
          (currentContext as { mitigationTitle?: string }).mitigationTitle || ''
        );
      case 'diagram':
        return (currentContext as { diagramName?: string }).diagramName || '';
      case 'space':
        return (currentContext as { spaceName?: string }).spaceName || '';
      default:
        return '';
    }
  })();

  const handleAccept = async (suggestion: AssistantSuggestion) => {
    try {
      await acceptSuggestion(suggestion);
      const actionLabel =
        suggestion.actionType === 'create_risk'
          ? t('assistant.actions.riskCreated', 'Risk created')
          : suggestion.actionType === 'create_mitigation'
            ? t('assistant.actions.mitigationCreated', 'Mitigation created')
            : t('assistant.actions.suggestionAccepted', 'Suggestion accepted');
      setSnackbar({
        open: true,
        message: `${actionLabel}: ${suggestion.title}`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Failed to accept suggestion:', error);
      setSnackbar({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : t(
                'assistant.actions.acceptFailed',
                'Failed to accept suggestion'
              ),
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleDismiss = (id: string) => {
    dismissSuggestion(id);
  };

  // Handler for clicking a connected component in InsightsTab
  const handleConnectedComponentClick = (component: Component) => {
    setComponentContext({
      componentId: component.id,
      componentName: component.name,
      componentType: component.type,
      confidentialityScore: component.confidentialityScore,
      integrityScore: component.integrityScore,
      availabilityScore: component.availabilityScore,
      dataCategories: component.dataCategories,
      environment: component.environment,
      spaceId: component.spaceId,
    });
  };

  // Handler for collapsed icon buttons - sets tab first, then expands
  const handleCollapsedTabClick = (
    tab: 'suggestions' | 'attacks' | 'insights'
  ) => {
    setActiveTab(tab);
    // Use setTimeout to ensure state update is processed before expanding
    setTimeout(() => {
      toggleCollapsed();
    }, 0);
  };

  // Collapsed state - just show icons
  if (collapsed) {
    return (
      <Box
        sx={{
          width: SIDEBAR_COLLAPSED_WIDTH,
          flexShrink: 0,
          borderLeft: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 1,
          overflow: 'auto',
        }}
      >
        <Tooltip title={t('assistant.title')} placement="left">
          <IconButton onClick={toggleCollapsed} size="small">
            <ChevronLeftIcon />
          </IconButton>
        </Tooltip>
        <Divider sx={{ my: 1, width: '100%' }} />
        <Tooltip title={t('assistant.tabs.insights')} placement="left">
          <IconButton
            size="small"
            onClick={() => handleCollapsedTabClick('insights')}
          >
            <SecurityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('assistant.tabs.suggestions')} placement="left">
          <IconButton
            size="small"
            onClick={() => handleCollapsedTabClick('suggestions')}
          >
            <Badge badgeContent={pendingSuggestionCount} color="error" max={9}>
              <LightbulbIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
        <Tooltip title={t('assistant.tabs.attacks')} placement="left">
          <IconButton
            size="small"
            onClick={() => handleCollapsedTabClick('attacks')}
          >
            <Badge
              badgeContent={attackScenarios.length}
              color="warning"
              max={9}
            >
              <RadarIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // Expanded state - full sidebar
  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        borderLeft: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <PsychologyIcon fontSize="small" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600} noWrap>
            {t('assistant.title')}
          </Typography>
          {contextName && (
            <Typography
              variant="caption"
              sx={{ opacity: 0.8, display: 'block' }}
              noWrap
            >
              {contextName}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={analyzeContext}
          disabled={isAnalyzing || currentContext.type === 'none'}
          sx={{ color: 'inherit' }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={toggleCollapsed}
          sx={{ color: 'inherit' }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Loading indicator */}
      {isAnalyzing && <LinearProgress />}

      {/* Mode Buttons - Insights first (default), then AI features */}
      <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
        <ButtonGroup variant="outlined" size="small" fullWidth>
          <Button
            variant={activeTab === 'insights' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('insights')}
            sx={{ fontSize: '0.7rem', py: 0.5 }}
          >
            {t('assistant.tabs.insights')}
          </Button>
          <Button
            variant={activeTab === 'suggestions' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('suggestions')}
            sx={{ fontSize: '0.7rem', py: 0.5 }}
          >
            <Badge
              badgeContent={pendingSuggestionCount}
              color="error"
              max={9}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.6rem',
                  minWidth: 14,
                  height: 14,
                },
              }}
            >
              {t('assistant.tabs.suggestions')}
            </Badge>
          </Button>
          <Button
            variant={activeTab === 'attacks' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('attacks')}
            sx={{ fontSize: '0.7rem', py: 0.5 }}
          >
            <Badge
              badgeContent={attackScenarios.length}
              color="warning"
              max={9}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.6rem',
                  minWidth: 14,
                  height: 14,
                },
              }}
            >
              {t('assistant.tabs.attacks')}
            </Badge>
          </Button>
        </ButtonGroup>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
        {currentContext.type === 'none' ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              color: 'text.secondary',
              py: 2,
            }}
          >
            <InfoIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">
              {t('assistant.selectContext')}
            </Typography>
          </Box>
        ) : (
          <>
            {activeTab === 'suggestions' && (
              <Box>
                {suggestions.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {isAnalyzing
                        ? t('assistant.analyzing')
                        : t('assistant.noSuggestions')}
                    </Typography>
                    {!isAnalyzing && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PlayArrowIcon />}
                        onClick={analyzeContext}
                        sx={{ mt: 1 }}
                      >
                        {t('assistant.analyze')}
                      </Button>
                    )}
                  </Box>
                ) : (
                  suggestions.map(suggestion => (
                    <SuggestionCard
                      key={suggestion.id}
                      suggestion={suggestion}
                      onAccept={handleAccept}
                      onDismiss={handleDismiss}
                    />
                  ))
                )}
              </Box>
            )}

            {activeTab === 'attacks' && (
              <Box>
                {attackScenarios.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('assistant.noAttackScenarios')}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RadarIcon />}
                      onClick={runAttackSimulation}
                      disabled={isAnalyzing}
                      sx={{ mt: 1 }}
                    >
                      {t('assistant.runSimulation')}
                    </Button>
                  </Box>
                ) : (
                  attackScenarios.map(scenario => (
                    <AttackScenarioCard key={scenario.id} scenario={scenario} />
                  ))
                )}
              </Box>
            )}

            {activeTab === 'insights' && (
              <InsightsTab
                context={currentContext}
                onComponentClick={handleConnectedComponentClick}
              />
            )}
          </>
        )}
      </Box>

      {/* Footer with last analyzed time */}
      <Box
        sx={{
          p: 1,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'action.hover',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {state.lastAnalyzedAt
            ? t('assistant.lastAnalyzed', {
                time: new Date(state.lastAnalyzedAt).toLocaleTimeString(),
              })
            : t('assistant.notAnalyzed')}
        </Typography>
      </Box>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
